// Cross-product condition picker + duplicates queue for the PDP.
//
// Two distinct jobs, and they must not interfere with each other:
//
//   1. Condition picker — only meaningful when this product actually has a
//      known condition (NM/LP/MP/HP). Most products currently don't (blank
//      metafield, variant titled "Default"), so the server renders no picker
//      for them and this script leaves the stock line alone. Getting this
//      wrong previously made in-stock cards read "Sold out" — never write a
//      sold-out state for a copy the server told us is available.
//
//   2. Duplicates queue — runs for every tagged product regardless of
//      condition data: if THIS copy is sold out but a sibling is in stock,
//      quietly point the page at the sibling instead.
//
// TWO REAL BUGS FOUND HERE 2026-08-25, both from the same wrong assumption
// (that "same grade" means "interchangeable"), on a Delibird with THREE LP
// copies plus one MP copy sharing a tag:
//
//   Bug A — this script used to unconditionally swap to "the first
//   available copy of the best grade" on EVERY page load, even when the
//   copy the shopper actually landed on was itself in stock. Click a
//   duplicate's tile, get a DIFFERENT duplicate's photo. Fixed: only ever
//   hand off to a sibling when the current copy is genuinely unavailable.
//
//   Bug B — far worse. Even after Bug A's fix, swapping grade (or the
//   sold-out hand-off) only ever swapped the FRONT photo directly via
//   img.src. The separate Front/Back toggle script
//   (assets/vault-pdp-thumbs.js) has its own private state and never learns
//   a swap happened, so it kept resolving Front/Back clicks against
//   whichever copy the PAGE was originally server-rendered for. Click MP,
//   then click Back: instead of MP's real back, you got the ORIGINAL LP
//   copy's back — a third, unrelated physical card's real photo, shown as
//   if it belonged to the copy on screen. On a store whose entire promise
//   is "the photo is the exact card you get," that is not a cosmetic bug.
//   Fixed by having applyCopy() hand the swapped-to copy's OWN back photo
//   (now included in the search feed, see templates/search.card-group.liquid)
//   to vault-pdp-thumbs.js via data attributes it checks before falling
//   back to its own server-rendered map. See that file for the other half.
//
// TWO ADDITIONS 2026-08-26 (AJ's "one tile per card" pass):
//
//   Live price — applyCopy() now writes the swapped-to copy's own display
//   price (money-formatted server-side, in the feed) into the PDP price.
//   Before, the shown price silently stayed the original copy's after a
//   condition switch. The cart always charged correctly (Shopify prices
//   off the real variant), but a shopper deciding whether to buy was
//   reading a stale number — and now that grid tiles say "From $X", the
//   PDP price MUST move when the condition does. No compare-at/sale
//   handling on purpose: no grouped product uses sale pricing; if that
//   ever changes, extend this rather than guessing now.
//
//   Copy picker — when one condition bucket holds more than one in-stock
//   copy (a real Delibird had three LP copies), a small select appears
//   with options "Copy A / Copy B / Copy C" (AJ's wording — SKUs mean
//   nothing to a shopper). It stays hidden in the common single-copy case.
//   The stock count line keeps describing the whole bucket, not the
//   selected copy — deliberately separate pieces of information.
//
// ONE MORE ADDITION 2026-09-12 (notify-me): updateCount() now also hides
// #NotifyMe-<sectionId> whenever it reports a real positive count. Found
// live testing the new notify-me box: a sold-out copy in a bucket with an
// in-stock sibling was showing "2 left" (this function, correctly) AND the
// notify-me box (server-rendered for the ORIGINAL sold copy, with nothing
// telling it a hand-off had happened) at the same time — two contradictory
// messages on one page. See snippets/vault-notify-me.liquid.
//
// DAMAGED (2026-10-05): DMG is a real fifth condition value now, but NOT a
// picker button (AJ: ordinary cards must not grow a DMG pill). A DMG copy's
// page renders the damaged callout and a hidden group div, so it takes the
// no-picker branch below. applyResults() restricts that branch to other DMG
// copies, so a damaged card never counts a clean sibling as "2 left" and a
// sold damaged copy never hands off to a clean one. The reverse already
// held: GRADES has no DMG bucket, so a clean card's picker ignores damaged
// siblings.
(function () {
  var GRADES = ['NM', 'LP', 'MP', 'HP'];

  function init(root) {
    if (root.dataset.cardGroupInit) return;
    root.dataset.cardGroupInit = 'true';

    var sectionId = root.getAttribute('data-section');
    var dataScript = document.getElementById('CardGroupData-' + sectionId);
    if (!dataScript) return;

    var info;
    try {
      info = JSON.parse(dataScript.textContent);
    } catch (e) {
      return;
    }
    if (!info.tag) return;

    var query = "tag:'" + info.tag.replace(/'/g, "\\'") + "'";
    var url = '/search?view=card-group&type=product&q=' + encodeURIComponent(query);

    fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (results) {
        applyResults(root, sectionId, Array.isArray(results) ? results : [], info);
      })
      .catch(function () {
        /* leave the server-rendered state exactly as-is on any failure */
      });
  }

  // The page's own copy, shaped like a feed entry, for when the search
  // index hasn't caught up with this product yet. No back/back_zoom: we
  // have no cheap way to compute those client-side for this page's own
  // product — rare edge case, self-resolving once the index catches up.
  function selfEntry(info) {
    return {
      variant_id: info.variantId,
      available: info.available,
      grade: info.grade,
      sku: info.sku,
      price: info.price,
      image: info.image,
      image_zoom: info.imageZoom
    };
  }

  function applyResults(root, sectionId, results, info) {
    // Damaged copies only ever pair with damaged copies (see header note).
    if (info.grade === 'DMG') {
      results = results.filter(function (p) { return p.grade === 'DMG'; });
    }

    var buttons = root.querySelectorAll('[data-grade]');
    var hasPicker = buttons.length > 0;

    if (hasPicker) {
      applyPicker(buttons, sectionId, results, info);
      return;
    }

    // No condition data: duplicates-queue behaviour. One bucket of every
    // in-stock copy, no grades to split by.
    var inStock = results.filter(function (p) { return p.available; });
    if (info.available && !inStock.some(function (p) { return p.variant_id === info.variantId; })) {
      inStock.unshift(selfEntry(info));
    }

    if (info.available) {
      // The server already rendered the correct state for THIS copy: leave
      // its photo and price alone (Bug A). Just correct the count to cover
      // the whole group, and offer the copy picker when there's a choice.
      updateCount(sectionId, inStock.length || 1);
      renderCopySelect(sectionId, inStock, info.variantId);
      return;
    }

    var others = inStock.filter(function (p) { return p.variant_id !== info.variantId; });
    if (!others.length) {
      renderCopySelect(sectionId, [], null);
      return;
    }

    applyCopy(sectionId, others[0]);
    updateCount(sectionId, others.length);
    renderCopySelect(sectionId, others, others[0].variant_id);
  }

  function applyPicker(buttons, sectionId, results, info) {
    var buckets = {};
    GRADES.forEach(function (g) { buckets[g] = []; });
    results.forEach(function (p) {
      if (buckets[p.grade]) buckets[p.grade].push(p);
    });

    // Make sure the copy we're already on is represented, even if the search
    // index hasn't caught up with it yet.
    if (buckets[info.grade] && !results.some(function (p) { return p.variant_id === info.variantId; })) {
      buckets[info.grade].push(selfEntry(info));
    }

    // In-stock copies per grade, hoisted so the copy picker can reuse each
    // bucket instead of recomputing it inside every click handler.
    var inStockByGrade = {};
    GRADES.forEach(function (g) {
      inStockByGrade[g] = buckets[g].filter(function (p) { return p.available; });
    });

    var bestGrade = null;
    GRADES.forEach(function (g) {
      if (!bestGrade && inStockByGrade[g].length) {
        bestGrade = g;
      }
    });

    // Which grade reads as "active" on load: this copy's own grade when it
    // is in stock (its photo is already on screen, server-rendered, and is
    // exactly the copy the shopper navigated to), otherwise whichever grade
    // we're about to hand off to.
    var activeGrade = info.available ? info.grade : bestGrade;

    buttons.forEach(function (btn) {
      var g = btn.dataset.grade;
      var inStock = inStockByGrade[g];
      btn.disabled = inStock.length === 0;
      btn.classList.toggle('active', g === activeGrade);
      btn.onclick = function () {
        if (btn.disabled) return;
        buttons.forEach(function (b) { b.classList.toggle('active', b === btn); });
        applyCopy(sectionId, inStock[0]);
        updateCount(sectionId, inStock.length);
        renderCopySelect(sectionId, inStock, inStock[0].variant_id);
      };
    });

    if (info.available) {
      // This exact copy is in stock: leave its photo and price alone, they
      // are already correct. Report the count for its own grade, and offer
      // the copy picker if its bucket holds siblings.
      var ownStock = inStockByGrade[info.grade] || [];
      updateCount(sectionId, ownStock.length || 1);
      renderCopySelect(sectionId, ownStock, info.variantId);
    } else if (bestGrade) {
      // This specific copy is sold out: a real hand-off to an in-stock
      // sibling, same honest case as the no-picker branch above.
      var bestStock = inStockByGrade[bestGrade];
      applyCopy(sectionId, bestStock[0]);
      updateCount(sectionId, bestStock.length);
      renderCopySelect(sectionId, bestStock, bestStock[0].variant_id);
    } else {
      // Only ever assert "sold out" when the server agrees this copy is gone.
      updateCount(sectionId, 0);
      renderCopySelect(sectionId, [], null);
    }
  }

  // The "Copy A / Copy B / Copy C" select. Hidden whenever the bucket holds
  // at most one copy — the overwhelmingly common case — so the page stays
  // exactly as it was before this existed. Every copy in the bucket shares
  // one grade and (by pricing rule) one price; what differs is which
  // physical card ships, and its photos, which applyCopy swaps in.
  function renderCopySelect(sectionId, bucket, selectedVariantId) {
    var wrap = document.getElementById('CopyPicker-' + sectionId);
    if (!wrap) return;
    var select = wrap.querySelector('[data-copy-select]');
    if (!select) return;

    if (!bucket || bucket.length <= 1) {
      wrap.hidden = true;
      select.innerHTML = '';
      return;
    }

    var letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    select.innerHTML = '';
    bucket.forEach(function (p, i) {
      var opt = document.createElement('option');
      opt.value = String(p.variant_id);
      opt.textContent = 'Copy ' + (letters[i] || String(i + 1));
      if (String(p.variant_id) === String(selectedVariantId)) opt.selected = true;
      select.appendChild(opt);
    });

    select.onchange = function () {
      var chosen = null;
      bucket.forEach(function (p) {
        if (String(p.variant_id) === select.value) chosen = p;
      });
      if (chosen) applyCopy(sectionId, chosen);
    };

    wrap.hidden = false;
  }

  function applyCopy(sectionId, copy) {
    var section = document.querySelector('[data-section="' + sectionId + '"]');
    if (!section || !copy) return;

    var stage = section.querySelector('.vault-pdp-media') || section;
    var img = stage.querySelector('.vault-holo-photo img');
    if (img && copy.image) {
      img.src = copy.image;
      img.srcset = '';
      if (copy.image_zoom) img.dataset.zoomSrc = copy.image_zoom;
    }

    // We are now showing a DIFFERENT physical card than the one this page
    // was originally rendered for. Hand its REAL front/back photos to
    // vault-pdp-thumbs.js via data attributes, which it checks before
    // falling back to its own server-rendered (and now-irrelevant) map for
    // the ORIGINAL copy. This is what makes Front/Back correct after a
    // swap, instead of quietly showing a third copy's photos (Bug B above).
    var thumbs = section.querySelector('[data-vault-thumbs]');
    if (thumbs) {
      thumbs.dataset.overrideFront = copy.image || '';
      thumbs.dataset.overrideFrontZoom = copy.image_zoom || '';
      thumbs.dataset.overrideBack = copy.back || '';
      thumbs.dataset.overrideBackZoom = copy.back_zoom || '';

      // Always land on the front view when the photo itself just changed
      // — don't leave Back looking pressed over what is now a front photo.
      var frontBtn = thumbs.querySelector('[data-side="front"]');
      var backBtn = thumbs.querySelector('[data-side="back"]');
      if (frontBtn) frontBtn.classList.add('active');
      if (backBtn) {
        backBtn.classList.remove('active');
        backBtn.hidden = !copy.back;
      }
      if (thumbs.dispatchEvent) {
        thumbs.dispatchEvent(new CustomEvent('vault:thumbs-override-front'));
      }
    }

    // Live price: the feed formats each copy's price server-side (same
    // money filter rules as the rest of the theme), so this is a straight
    // text write. Falls back to the original number untouched if the copy
    // has no price (an old cached feed response, for instance) — a stale
    // price is exactly what this line exists to prevent, but a blank one
    // would be worse.
    if (copy.price) {
      var priceWrap = document.getElementById('price-' + sectionId);
      var priceEl = priceWrap
        ? priceWrap.querySelector('.price-item--regular')
        : section.querySelector('.price__regular .price-item--regular');
      if (priceEl) priceEl.textContent = copy.price;
    }

    var form = document.getElementById('product-form-' + sectionId);
    if (form) {
      var idInput = form.querySelector('input[name="id"]');
      if (idInput && copy.variant_id) {
        idInput.value = copy.variant_id;
        idInput.disabled = false;
      }
    }

    var submitBtn = document.getElementById('ProductSubmitButton-' + sectionId);
    if (submitBtn) {
      submitBtn.disabled = false;
      var label = submitBtn.querySelector('span');
      if (label) label.textContent = 'Add to cart';
    }
  }

  function updateCount(sectionId, n) {
    var el = document.getElementById('StockCount-' + sectionId);
    if (!el) return;
    if (n <= 0) {
      el.textContent = 'Sold out';
    } else if (n === 1) {
      el.textContent = '1 left';
    } else if (n >= 5) {
      el.textContent = '5+ in stock';
    } else {
      el.textContent = n + ' left';
    }

    // n > 0 here always means a real in-stock copy is now on screen (either
    // this exact copy was in stock all along, or a hand-off just happened)
    // — the "notify me" box below only makes sense while nothing here is
    // actually buyable, so hide it the instant that stops being true. Never
    // re-shown by this function on purpose: clicking between grades after
    // this always means the shopper is looking at something available.
    var notify = document.getElementById('NotifyMe-' + sectionId);
    if (notify && n > 0) notify.hidden = true;
  }

  function scan() {
    document.querySelectorAll('[data-vault-card-group]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
  document.addEventListener('shopify:section:load', scan);
})();
